import type { PermissionGroup, PermissionModule, PermissionResource } from './types'

export const ROLE_WRITE_PERM = 'identity:role:write'
export const USER_READ_PERM = 'identity:user:read'
export const USER_WRITE_PERM = 'identity:user:write'
export const MAIL_ACCOUNT_MANAGE_PERM = 'mail:account:manage'
export const MAIL_PROVIDER_MANAGE_PERM = 'mail:provider:manage'

const VIEW_ACTIONS = new Set(['view', 'read'])
const SENSITIVE_ACTIONS = new Set(['delete', 'export', 'approve', 'sensitive_read', 'owner_approve'])

export type ApiPermission = { id: string; code: string; title?: string; description?: string; module?: string }

export type PermissionCatalog = {
  groups: PermissionGroup[]
  modules: PermissionModule[]
  idByCode: Record<string, string>
  codeById: Record<string, string>
  titleByCode: Record<string, string>
  descriptionByCode: Record<string, string>
}

export const permissionCode = (module: string, resource: string, action: string) => `${module}.${resource}.${action}`

export function actionCode(module: PermissionModule, resource: PermissionResource, action: string) {
  return resource.codes?.[action] ?? permissionCode(module.key, resource.key, action)
}

export type PermissionRef = { module: PermissionModule; resource: PermissionResource; action: string; code: string }

/** Group by the first segment, resource is the middle, action is the last token. */
export function splitPermissionCode(code: string): { module: string; resource: string; action: string } {
  const sep = code.includes(':') ? ':' : '.'
  const parts = code.split(sep).filter(Boolean)
  if (parts.length === 0) return { module: 'general', resource: 'general', action: 'view' }
  const action = parts[parts.length - 1] ?? 'view'
  const head = parts.slice(0, -1)
  if (head.length === 0) return { module: action, resource: action, action }
  if (head.length === 1) return { module: head[0] ?? 'general', resource: head[0] ?? 'general', action }
  return { module: head[0] ?? 'general', resource: head.slice(1).join(sep), action }
}

export function emptyPermissionCatalog(): PermissionCatalog {
  return { groups: [], modules: [], idByCode: {}, codeById: {}, titleByCode: {}, descriptionByCode: {} }
}

/** Satır etiketi: BE title → description → code */
export function permissionDisplayLabel(code: string, catalog?: PermissionCatalog | null, fallback = '') {
  if (!code) return fallback
  return catalog?.titleByCode[code] || catalog?.descriptionByCode[code] || fallback || code
}

function buildModulesFromRows(
  rows: readonly ApiPermission[],
  groupMeta?: Map<string, { title: string; description: string; sortOrder: number }>,
): PermissionModule[] {
  const modules = new Map<
    string,
    Map<string, { action: string; code: string; description: string; sensitive: boolean }[]>
  >()

  for (const row of rows) {
    const code = row.code?.trim()
    if (!code) continue
    const parsed = splitPermissionCode(code)
    const moduleKey = row.module?.trim() || parsed.module
    const resources = modules.get(moduleKey) ?? new Map()
    const actions: { action: string; code: string; description: string; sensitive: boolean }[] = resources.get(parsed.resource) ?? []
    if (!actions.some((a) => a.code === code)) {
      actions.push({
        action: parsed.action,
        code,
        description: row.description?.trim() || '',
        sensitive: SENSITIVE_ACTIONS.has(parsed.action) || parsed.action.includes('sensitive'),
      })
    }
    resources.set(parsed.resource, actions)
    modules.set(moduleKey, resources)
  }

  return [...modules.entries()]
    .map(([key, resources]) => {
      const meta = groupMeta?.get(key)
      return {
        key,
        title: meta?.title,
        description: meta?.description,
        sortOrder: meta?.sortOrder,
        resources: [...resources.entries()].map(([resourceKey, actions]) => ({
          key: resourceKey,
          actions: actions.map((a) => a.action),
          codes: Object.fromEntries(actions.map((a) => [a.action, a.code])),
          descriptions: Object.fromEntries(actions.map((a) => [a.action, a.description])),
          sensitive: actions.filter((a) => a.sensitive).map((a) => a.action),
        })),
      }
    })
    .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999) || a.key.localeCompare(b.key))
}

export function catalogFromPermissions(rows: readonly ApiPermission[]): PermissionCatalog {
  const idByCode: Record<string, string> = {}
  const codeById: Record<string, string> = {}
  const titleByCode: Record<string, string> = {}
  const descriptionByCode: Record<string, string> = {}

  for (const row of rows) {
    const code = row.code?.trim()
    if (!code) continue
    if (row.id) {
      idByCode[code] = row.id
      codeById[row.id] = code
    }
    if (row.title?.trim()) titleByCode[code] = row.title.trim()
    if (row.description?.trim()) descriptionByCode[code] = row.description.trim()
  }

  return {
    groups: [],
    idByCode,
    codeById,
    titleByCode,
    descriptionByCode,
    modules: buildModulesFromRows(rows),
  }
}

export function catalogFromGroups(
  groupsRaw: readonly {
    key?: string
    title?: string
    description?: string
    sort_order?: number
    sortOrder?: number
    permissions?: readonly ApiPermission[]
  }[],
): PermissionCatalog {
  const idByCode: Record<string, string> = {}
  const codeById: Record<string, string> = {}
  const titleByCode: Record<string, string> = {}
  const descriptionByCode: Record<string, string> = {}
  const groupMeta = new Map<string, { title: string; description: string; sortOrder: number }>()
  const flat: ApiPermission[] = []

  const groups: PermissionGroup[] = [...groupsRaw]
    .map((g, index) => {
      const key = (g.key ?? '').trim() || `group-${index}`
      const title = (g.title ?? '').trim() || key
      const description = (g.description ?? '').trim()
      const sortOrder = typeof g.sort_order === 'number' ? g.sort_order : typeof g.sortOrder === 'number' ? g.sortOrder : index * 10
      groupMeta.set(key, { title, description, sortOrder })
      const permissions = (g.permissions ?? [])
        .map((row) => {
          const code = row.code?.trim()
          if (!code) return null
          const id = row.id?.trim() || ''
          const permTitle = row.title?.trim() || ''
          const desc = row.description?.trim() || ''
          if (id) {
            idByCode[code] = id
            codeById[id] = code
          }
          if (permTitle) titleByCode[code] = permTitle
          if (desc) descriptionByCode[code] = desc
          const item = {
            id,
            code,
            title: permTitle,
            description: desc,
            module: row.module?.trim() || key,
          }
          flat.push(item)
          return item
        })
        .filter((p): p is NonNullable<typeof p> => !!p)
      return { key, title, description, sortOrder, permissions }
    })
    .sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))

  return {
    groups,
    idByCode,
    codeById,
    titleByCode,
    descriptionByCode,
    modules: buildModulesFromRows(flat, groupMeta),
  }
}

export function findPermission(code: string, catalog: readonly PermissionModule[]): PermissionRef | null {
  for (const module of catalog) {
    for (const resource of module.resources) {
      for (const action of resource.actions) {
        if (actionCode(module, resource, action) === code) return { module, resource, action, code }
      }
    }
  }
  return null
}

export const resourceCodes = (module: PermissionModule, resource: PermissionResource) =>
  resource.actions.map((a) => actionCode(module, resource, a))

export const moduleCodes = (module: PermissionModule) => module.resources.flatMap((r) => resourceCodes(module, r))

export const allCodes = (catalog: readonly PermissionModule[]) => catalog.flatMap(moduleCodes)

export const isSensitive = (ref: PermissionRef) => ref.resource.sensitive?.includes(ref.action) ?? false

export function prerequisitesOf(code: string, catalog: readonly PermissionModule[]): string[] {
  const ref = findPermission(code, catalog)
  if (!ref || VIEW_ACTIONS.has(ref.action)) return []
  const view = ref.resource.actions.find((a) => VIEW_ACTIONS.has(a))
  if (!view) return []
  return [actionCode(ref.module, ref.resource, view)]
}

export function missingPrerequisites(code: string, granted: ReadonlySet<string>, catalog: readonly PermissionModule[]): string[] {
  return prerequisitesOf(code, catalog).filter((p) => !granted.has(p))
}

export function diffPermissions(before: readonly string[], after: readonly string[]) {
  const prev = new Set(before)
  const next = new Set(after)
  return {
    added: after.filter((c) => !prev.has(c)),
    removed: before.filter((c) => !next.has(c)),
  }
}

export function toggleMany(granted: readonly string[], codes: readonly string[]): string[] {
  const set = new Set(granted)
  const allOn = codes.length > 0 && codes.every((c) => set.has(c))
  for (const c of codes) {
    if (allOn) set.delete(c)
    else set.add(c)
  }
  return [...set]
}
